export interface ParsedApiError {
  code?: string;
  params?: Record<string, unknown>;
  message?: string;
}

/** Thrown by API clients when a request fails with a structured error body. */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly params?: Record<string, unknown>;

  constructor(message: string, status: number, parsed?: ParsedApiError | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = parsed?.code;
    this.params = parsed?.params as Record<string, unknown> | undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Parse a FastAPI error body into a stable code and optional params. */
export function parseApiError(text: string): ParsedApiError | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const json = JSON.parse(trimmed) as { detail?: unknown };
    const detail = json.detail;
    if (typeof detail === "string" && detail.trim()) {
      return { message: detail.trim() };
    }
    if (isRecord(detail) && typeof detail.code === "string") {
      const params = isRecord(detail.params) ? detail.params : undefined;
      return { code: detail.code, params };
    }
  } catch {
    // plain-text error body
  }
  return { message: trimmed };
}

/** Extract a user-facing message from a FastAPI-style error response body. */
export function parseApiErrorBody(text: string, fallback: string): string {
  const parsed = parseApiError(text);
  if (parsed?.message) {
    return parsed.message;
  }
  if (parsed?.code) {
    return parsed.code;
  }
  return fallback;
}
